import { useState, useEffect } from 'react'
import { Plus, Search, Edit2, Trash2, Package } from 'lucide-react'
import { apiClient } from '../../services/api'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { Unit } from '../../types/api'

interface UnitFormData {
  name: string
  short_name: string
  description: string
  sort_order: string
  allow_decimal: boolean
  is_active: boolean
}

export const UnitManagement = () => {
  const [units, setUnits] = useState<Unit[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const [formData, setFormData] = useState<UnitFormData>({
    name: '',
    short_name: '',
    description: '',
    sort_order: '0',
    allow_decimal: false,
    is_active: true
  })

  useEffect(() => {
    loadUnits()
  }, [])

  const loadUnits = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getUnits()
      const unitsData = response.units || response.data?.units || []
      setUnits(unitsData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load units')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Convert boolean fields to integers for API
      const submitData = {
        ...formData,
        allow_decimal: formData.allow_decimal ? 1 : 0,
        is_active: formData.is_active ? 1 : 0
      }

      if (editingUnit) {
        await apiClient.updateUnit(editingUnit.id, submitData)
      } else {
        await apiClient.createUnit(submitData)
      }
      await loadUnits()
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save unit')
    }
  }

  const handleEdit = (unit: Unit) => {
    setEditingUnit(unit)
    setFormData({
      name: unit.name,
      short_name: unit.short_name,
      description: unit.description || '',
      sort_order: unit.sort_order.toString(),
      allow_decimal: unit.allow_decimal === 1,
      is_active: unit.is_active === 1
    })
    setShowForm(true)
  }

  const handleDelete = async (unitId: string) => {
    if (!confirm('Are you sure you want to delete this unit?')) return

    try {
      await apiClient.deleteUnit(unitId)
      await loadUnits()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete unit')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      short_name: '',
      description: '',
      sort_order: '0',
      allow_decimal: false,
      is_active: true
    })
    setEditingUnit(null)
    setShowForm(false)
  }

  const filteredUnits = (units || []).filter(unit =>
    unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    unit.short_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    unit.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Unit Management</h1>
          <p className="text-gray-600">Manage units of measurement for your products</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Unit
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Search */}
      <Card className="p-6">
        <div className="max-w-md relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search Units</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, short name or description..."
              className="pl-10"
            />
          </div>
        </div>
      </Card>

      {/* Unit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {editingUnit ? 'Edit Unit' : 'Add New Unit'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., Pieces, Kilogram, Liter"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Name *</label>
                <input
                  type="text"
                  required
                  value={formData.short_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, short_name: e.target.value.toLowerCase() }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., pcs, kg, ltr"
                  disabled={!!editingUnit}
                />
                {editingUnit && (
                  <p className="text-xs text-gray-500 mt-1">Short name cannot be changed after creation</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Optional description for this unit"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allow_decimal}
                    onChange={(e) => setFormData(prev => ({ ...prev, allow_decimal: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Allow Decimal Quantities</span>
                </label>
                <p className="text-xs text-gray-500 pl-6">
                  Enable for weighable units (kg, g, L, ml). Disable for countable units (pieces, boxes, packs)
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active Unit</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md"
                >
                  {editingUnit ? 'Update' : 'Create'} Unit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUnits.map((unit) => (
          <Card key={unit.id} className="p-6 card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-orange-500" />
                <h3 className="text-lg font-semibold text-gray-800">{unit.name}</h3>
              </div>
              <Badge variant={unit.is_active ? 'success' : 'secondary'}>
                {unit.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            <div className="mb-3 flex items-center gap-2">
              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-sm font-mono rounded">
                {unit.short_name}
              </span>
              <span className={`inline-flex items-center px-2 py-1 text-xs rounded ${
                unit.allow_decimal
                  ? 'bg-blue-50 text-blue-700'
                  : 'bg-purple-50 text-purple-700'
              }`}>
                {unit.allow_decimal ? '0.1, 0.2, 0.3...' : '1, 2, 3...'}
              </span>
            </div>

            {unit.description && (
              <p className="text-gray-600 text-sm mb-4">{unit.description}</p>
            )}

            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <span>Sort Order: {unit.sort_order}</span>
              <span>Created: {new Date(unit.created_at).toLocaleDateString()}</span>
            </div>

            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(unit)}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <Edit2 className="h-3 w-3" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(unit.id)}
                className="flex-1 flex items-center justify-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {filteredUnits.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No units found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm ? 'Try adjusting your search terms' : 'Create your first unit to get started'}
          </p>
          {!searchTerm && (
            <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Unit
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
